import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Shield, Check, Star, Quote } from "lucide-react";
import { useState } from "react";
import { INDIVIDUAL_SERVICES } from "../data/individualServicesData";
import { SERVICE_BANNERS, SERVICE_TESTIMONIALS, GENERIC_TESTIMONIALS } from "../data/serviceEnhancements";
import { ImageCluster } from "../components/serviceVisuals/ImageClusters";
import { Navbar } from "../components/layout/Navbar";
import { Footer } from "../components/layout/Footer";
import { LeadForm } from "../components/sections/LeadForm";
import { AppointmentModal } from "../components/common/AppointmentModal";
import { SEO } from "../components/common/SEO";
import { SERVICE_SEO } from "../data/seoData";

export default function IndividualServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [appointmentOpen, setAppointmentOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);

  const service = INDIVIDUAL_SERVICES[slug];
  
  // Get service-specific data
  const { accent, icon: HeroIcon, title, tagline, heroDesc, offerings, problems, whyUs, steps, category, categorySlug } = service || {};
  
  // Get service-specific banner image (fallback to category default if not found)
  const bannerImage = SERVICE_BANNERS[slug] || SERVICE_BANNERS[categorySlug] || "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab";
  
  // Get service-specific testimonials (fallback to generic if not found)
  const testimonials = SERVICE_TESTIMONIALS[slug] || GENERIC_TESTIMONIALS;
  
  // Duplicate testimonials for seamless infinite scroll
  const duplicatedTestimonials = [...testimonials, ...testimonials];
  
  // Get SEO data for this specific service
  const seoKey = slug;
  const seoData = SERVICE_SEO[seoKey] || SERVICE_SEO[`${categorySlug}/${slug}`] || {
    title: `${title} | SwitchYourVendor`,
    description: heroDesc,
    keywords: `${title}, ${category} services, business services India`
  };
  
  // Early return after all hooks
  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-heading text-3xl font-black mb-3">Page Not Found</h1>
          <button onClick={() => navigate("/")} className="text-red-600 font-bold font-body">Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <SEO 
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical={`https://switchyourvendor.com/${slug}`}
      />
      <AppointmentModal open={appointmentOpen} onClose={() => setAppointmentOpen(false)} accent={accent} />

      {/* Main Navbar - Dynamic theme color based on category */}
      <Navbar 
        onOpenAppointment={() => setAppointmentOpen(true)} 
        themeColor={accent}
        activeCategory={categorySlug}
      />

      {/* HERO */}
      <section className="pt-16 pb-10 relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${accent}06 0%, #ffffff 50%, ${accent}04 100%)` }}>
        <div className="max-w-7xl mx-auto px-6">
          {/* Breadcrumbs - Simplified: Home / Service */}
          <div className="flex items-center gap-2 text-xs mb-4 font-body flex-wrap">
            <span className="text-slate-400 font-semibold cursor-pointer hover:text-slate-600" onClick={() => navigate("/")}>Home</span>
            <span className="text-slate-300">/</span>
            <span className="font-bold" style={{ color: accent }}>{title}</span>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4" style={{ background: `${accent}10`, border: `1px solid ${accent}25` }}>
                <span className="w-1.5 h-1.5 rounded-full animate-pulse-dot" style={{ background: "#22c55e" }} />
                <span className="text-[11px] font-bold font-body" style={{ color: accent }}>{tagline}</span>
              </div>

              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black leading-[1.08] tracking-tight mb-4">
                {title.split(" ").slice(0, -1).join(" ")}{" "}
                <span className="bg-clip-text text-transparent" style={{ backgroundImage: `linear-gradient(135deg, ${accent}, ${accent}aa)` }}>
                  {title.split(" ").slice(-1)[0]}
                </span>
              </h1>

              <p className="text-slate-500 text-sm md:text-base leading-relaxed mb-6 max-w-md font-body">{heroDesc}</p>

              <div className="flex flex-col sm:flex-row gap-3">
                <button data-testid="individual-hero-cta" onClick={() => setAppointmentOpen(true)} className="font-extrabold text-sm px-6 py-3 rounded-2xl text-white transition-all active:scale-95 flex items-center justify-center gap-2 font-body" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 8px 30px ${accent}30` }}>
                  Book Your Appointment <ArrowRight size={16} />
                </button>
                <button data-testid="individual-hero-scroll" onClick={() => document.getElementById("ind-contact")?.scrollIntoView({ behavior: "smooth" })} className="border-2 border-slate-200 text-slate-700 font-extrabold text-sm px-6 py-3 rounded-2xl transition-all active:scale-95 font-body bg-white hover:border-slate-300">
                  Send Inquiry
                </button>
              </div>
            </motion.div>

            {/* Right Side: Animated Emojis + Unique Service Stats */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="hidden lg:block relative">
              {/* Background Animated Emojis */}
              <div className="absolute inset-0 opacity-40">
                <ImageCluster slug={slug} accent={accent} />
              </div>
              
              {/* Service-Specific Stats/Benefits Cards */}
              <div className="relative z-10 grid grid-cols-2 gap-3">
                {whyUs.map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 + i * 0.1 }}
                    className="rounded-2xl p-5 backdrop-blur-sm"
                    style={{
                      background: i === 0 
                        ? `linear-gradient(145deg, ${accent}, ${accent}dd)` 
                        : "rgba(255, 255, 255, 0.95)",
                      border: i === 0 ? "none" : "1.5px solid #e2e8f0",
                      boxShadow: i === 0 
                        ? `0 12px 40px ${accent}25` 
                        : "0 2px 12px rgba(0,0,0,0.04)"
                    }}
                  >
                    <h4 
                      className="text-sm font-extrabold mb-1 font-body" 
                      style={{ color: i === 0 ? "white" : "#0f172a" }}
                    >
                      {item.title}
                    </h4>
                    <p 
                      className="text-[11px] leading-relaxed font-body" 
                      style={{ color: i === 0 ? "rgba(255,255,255,0.75)" : "#94a3b8" }}
                    >
                      {item.desc}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT WE OFFER */}
      <section className="py-10 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader accent={accent} tag="What We Offer" title="Services Included" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {offerings.map((o, i) => (
              <motion.div key={o.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }} className="bg-white rounded-2xl p-6 border border-slate-100 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${accent}10` }}>
                  <Check size={18} style={{ color: accent }} />
                </div>
                <h3 className="text-sm font-extrabold text-slate-900 mb-1.5 font-body">{o.title}</h3>
                <p className="text-[12px] text-slate-500 leading-relaxed font-body">{o.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PROBLEMS WE SOLVE */}
      <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader accent={accent} tag="Problems We Solve" title="Why You Need This" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {problems.map((p, i) => {
              const Icon = p.icon;
              return (
                <motion.div key={p.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3" style={{ background: `${accent}10` }}>
                    <Icon size={20} style={{ color: accent }} />
                  </div>
                  <h3 className="text-sm font-extrabold text-slate-900 mb-1.5 font-body">{p.title}</h3>
                  <p className="text-[12px] text-slate-500 leading-relaxed font-body">{p.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-10 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <SectionHeader accent={accent} tag="The SYV Advantage" title="Why Choose Us" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {whyUs.map((item, i) => (
              <motion.div key={item.title} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }} className="bg-white rounded-2xl p-6 border border-slate-100 text-center hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ background: `${accent}10` }}>
                  <Shield size={18} style={{ color: accent }} />
                </div>
                <h3 className="text-sm font-extrabold text-slate-900 mb-1 font-body">{item.title}</h3>
                <p className="text-[12px] text-slate-500 leading-relaxed font-body">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-10 relative overflow-hidden" style={{ background: "linear-gradient(160deg, #0f172a, #1a1035)" }}>
        <div className="absolute top-[10%] left-[5%] w-[300px] h-[300px] rounded-full pointer-events-none" style={{ background: `radial-gradient(circle, ${accent}10, transparent 70%)` }} />
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="font-bold text-xs tracking-[0.2em] uppercase font-body" style={{ color: `${accent}cc` }}>Our Process</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-black text-white mt-2 tracking-tight">How It Works</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 z-0" style={{ background: `linear-gradient(to right, ${accent}40, ${accent}20)` }} />
            {steps.map((step, i) => {
              const Icon = step.icon;
              return (
                <motion.div key={step.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="flex flex-col items-center text-center relative z-10">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 0 0 6px ${accent}18, 0 8px 30px ${accent}30` }}>
                    <Icon size={24} className="text-white" />
                  </div>
                  <span className="text-[10px] font-extrabold tracking-[0.15em] mb-1 font-body" style={{ color: `${accent}cc` }}>STEP 0{i + 1}</span>
                  <h3 className="text-sm font-extrabold text-white mb-1.5 font-body">{step.title}</h3>
                  <p className="text-[12px] text-slate-500 leading-relaxed font-body">{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SWITCH VENDOR CTA */}
      <section className="py-12" style={{ background: `linear-gradient(135deg, ${accent}06, white)` }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-black mb-3 tracking-tight">
            Thinking of switching your current vendor?
          </h2>
          <p className="text-slate-500 text-sm font-body mb-3">
            We help you compare multiple vendors before you decide — so you always get the best deal.
          </p>
          <div className="inline-flex items-center gap-2 rounded-2xl px-5 py-2.5 mb-6" style={{ background: `${accent}08`, border: `1px solid ${accent}20` }}>
            <Shield size={16} style={{ color: accent }} />
            <span className="text-[13px] font-bold font-body" style={{ color: accent }}>Verified vendors only</span>
          </div>
          <br />
          <button data-testid="individual-switch-btn" onClick={() => setAppointmentOpen(true)} className="font-extrabold text-sm px-7 py-3.5 rounded-2xl text-white transition-all active:scale-95 inline-flex items-center gap-2 font-body" style={{ background: `linear-gradient(135deg, ${accent}, ${accent}cc)`, boxShadow: `0 8px 30px ${accent}30` }}>
            Switch Vendor Now <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section id="ind-contact" className="py-10" style={{ background: `linear-gradient(135deg, ${accent}04, white)` }}>
        <div className="max-w-2xl mx-auto px-6">
          <div className="text-center mb-8">
            <span className="font-bold text-xs tracking-[0.2em] uppercase font-body" style={{ color: accent }}>Free Consultation</span>
            <h2 className="font-heading text-2xl sm:text-3xl font-black mt-2 mb-2 tracking-tight">Get Started with {title}</h2>
            <p className="text-slate-500 text-sm font-body">Tell us about your needs. We'll respond within 24 hours.</p>
          </div>
          <LeadForm accent={accent} preselectedService={title} />
        </div>
      </section>

      {/* CONTINUOUS SCROLLING TESTIMONIALS - ABOVE FOOTER */}
      <section className="py-12 bg-white overflow-hidden border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-8">
            <span className="font-bold text-xs tracking-[0.2em] uppercase font-body" style={{ color: accent }}>Client Success Stories</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-black mt-2 tracking-tight">What Our Clients Say</h2>
            <p className="text-slate-500 text-sm mt-3 font-body">Real results from real businesses using our {title.toLowerCase()}</p>
          </div>

          {/* Continuous Scrolling Container */}
          <div 
            className="relative overflow-hidden"
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            {/* Infinite Scroll Track */}
            <div 
              className={`flex gap-6 ${isPaused ? '' : 'animate-scroll-testimonials'}`}
              style={{
                width: 'max-content',
              }}
            >
              {duplicatedTestimonials.map((testimonial, i) => (
                <div
                  key={i}
                  className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-7 border border-slate-200 hover:shadow-xl hover:border-slate-300 transition-all duration-300 group w-[350px] md:w-[400px] flex-shrink-0"
                  data-testid={`testimonial-${i}`}
                >
                  {/* Quote Icon */}
                  <div className="mb-4 opacity-20 group-hover:opacity-30 transition-opacity">
                    <Quote size={32} style={{ color: accent }} />
                  </div>

                  {/* Stars */}
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, idx) => (
                      <Star key={idx} size={16} fill={accent} stroke={accent} />
                    ))}
                  </div>

                  {/* Testimonial Text */}
                  <p className="text-sm text-slate-700 leading-relaxed mb-6 font-body italic">
                    "{testimonial.text}"
                  </p>

                  {/* Client Info */}
                  <div className="flex items-center gap-3 pt-4 border-t border-slate-200">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base shadow-md flex-shrink-0" 
                      style={{ background: `linear-gradient(135deg, ${accent}, ${accent}dd)` }}
                    >
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <p className="text-sm font-extrabold text-slate-900 font-body">{testimonial.name}</p>
                      <p className="text-xs text-slate-500 font-body">{testimonial.role}</p>
                      <p className="text-xs text-slate-400 font-body">{testimonial.company}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Gradient Overlays for Visual Effect */}
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent pointer-events-none z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent pointer-events-none z-10"></div>
          </div>
        </div>
      </section>

      {/* Main Footer - Consistent across all pages */}
      <Footer onOpenAppointment={() => setAppointmentOpen(true)} />
    </div>
  );
}

function SectionHeader({ accent, tag, title }) {
  return (
    <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-8">
      <span className="font-bold text-xs tracking-[0.2em] uppercase font-body" style={{ color: accent }}>{tag}</span>
      <h2 className="font-heading text-2xl sm:text-3xl font-black mt-2 tracking-tight">{title}</h2>
    </motion.div>
  );
}
